import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";

export default function PartnerBillingPage() {
    const { api } = useAuth();
    const [b, setB] = useState(null);

    useEffect(() => {
        api.get("/v2/partner/billing").then(r => setB(r.data)).catch(() => {});
    }, [api]);

    if (!b) return <div className="text-gray-500 text-sm">Loading…</div>;

    const wsPct = b.workspace_cap ? (b.usage.workspaces / b.workspace_cap) * 100 : null;
    const empPct = b.employee_cap ? (b.usage.employees / b.employee_cap) * 100 : null;

    return (
        <div data-testid="partner-billing-page">
            <div className="mb-8">
                <h1 className="text-3xl font-display font-bold text-white">Billing</h1>
                <p className="text-sm text-gray-500 mt-1 font-mono uppercase tracking-wider">{b.billing_tier} · {b.billing_model.replace(/_/g, " ")}</p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-8">
                <Kpi label="Active workspaces" value={b.usage.workspaces} cap={b.workspace_cap} pct={wsPct} testid="ws-usage" />
                <Kpi label="Employees assessed" value={b.usage.employees} cap={b.employee_cap} pct={empPct} testid="emp-usage" />
                <div className="bg-card border border-border/40 p-5">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-body">Projected next invoice</p>
                    <p className="text-3xl font-display font-bold text-white font-mono mt-2">
                        ${b.projected_next_invoice.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 font-mono">
                        @ ${b.rate.rate}/{b.rate.unit}
                    </p>
                </div>
            </div>

            {(b.overage.workspace || b.overage.employee) && (
                <div className="bg-amber-500/5 border border-amber-500/30 p-4 mb-6" data-testid="overage-warning">
                    <p className="text-sm text-amber-300 font-display">Overage warning</p>
                    <p className="text-xs text-amber-200 mt-1">
                        You've reached your tier cap. Contact your platform admin to increase the cap before provisioning more.
                    </p>
                </div>
            )}

            <div className="bg-card border border-border/40 p-6">
                <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Billing model</p>
                <p className="text-sm text-gray-300 font-body">
                    {b.billing_model === "per_workspace" && `You pay per workspace. Each workspace costs $${b.rate.rate}/month, billed monthly.`}
                    {b.billing_model === "per_employee" && `You pay per employee assessed annually. $${b.rate.rate} per employee per year, billed quarterly.`}
                    {b.billing_model === "flat_retainer" && `Flat retainer of $${b.rate.rate.toLocaleString()}/quarter. Includes up to ${b.workspace_cap || "—"} workspaces and ${b.employee_cap || "—"} employees.`}
                </p>
            </div>
        </div>
    );
}

function Kpi({ label, value, cap, pct, testid }) {
    const over = pct != null && pct >= 100;
    return (
        <div className={`bg-card border ${over ? "border-red-500/40" : "border-border/40"} p-5`} data-testid={testid}>
            <p className="text-xs uppercase tracking-wider text-gray-500 font-body">{label}</p>
            <p className="text-3xl font-display font-bold text-white font-mono mt-2">{value}{cap != null && <span className="text-gray-600 text-base"> / {cap}</span>}</p>
            {pct != null && (
                <div className="mt-3 h-1 bg-white/5">
                    <div className={`h-1 ${over ? "bg-red-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
            )}
        </div>
    );
}
